import React, { FunctionComponent, memo } from 'react';

import { useFormikContext } from 'formik';

import { useTranslation } from 'react-i18next';
import {
  OutreachConfigSections,
  OutreachTimeFormFields,
  SupportOutreachTriggerFormFields,
} from '../../../../../types';
import { FrequencyUnits } from '../../../helpers';
import FormattedMessage from '../../../../../../../utils/components/FormattedMessage';
import { OutreachTypes } from '../../../../../../../urls';

const renderDateTime = (date: Date) => {
  const formattedDate = new Date(date).toLocaleDateString();
  const formattedTime = new Date(date).toLocaleTimeString();
  return `${formattedDate} ${formattedTime}`;
};

const mapOutreachTypeToSection: Record<
  OutreachTypes,
  {
    time: OutreachConfigSections;
    trigger: OutreachConfigSections;
  }
> = {
  [OutreachTypes.Email]: {
    time: OutreachConfigSections.SupportOutreachTime,
    trigger: OutreachConfigSections.SupportOutreachTrigger,
  },
  [OutreachTypes.Webhooks]: {
    time: OutreachConfigSections.SupportOutreachTime,
    trigger: OutreachConfigSections.SupportOutreachTrigger,
  },
  [OutreachTypes.Push]: {
    time: OutreachConfigSections.SubscriberOutreachTime,
    trigger: OutreachConfigSections.SubscriberOutreachTrigger,
  },
};

const SupportTimeTriggerFooter: FunctionComponent<{
  outreachType: OutreachTypes;
}> = ({ outreachType = OutreachTypes.Email }) => {
  const { values } = useFormikContext() as { values: Record<string, any> };
  const { t } = useTranslation();
  const sections = mapOutreachTypeToSection[outreachType];
  const timeSection = values[sections.time];
  const triggerSection = values[sections.trigger];
  const rawStartDate = timeSection[OutreachTimeFormFields.From];
  const startDate = new Date(rawStartDate);
  const endDate = timeSection[OutreachTimeFormFields.To];
  const recurrenceUnit =
    triggerSection[SupportOutreachTriggerFormFields.TimeRepeatEveryUnit];
  const recurrence =
    triggerSection[SupportOutreachTriggerFormFields.TimeRepeatEvery] || 0;
  const isOneTimeOnly = Boolean(
    triggerSection[SupportOutreachTriggerFormFields.IsOneTimeOnly],
  );

  const renderStartDateTime = () => {
    return renderDateTime(startDate);
  };

  const renderEndDateTime = () => {
    if (!endDate) {
      return t('outreach.form.subscriberOutreachTimeSection.neverEnds');
    }
    return renderDateTime(endDate);
  };

  const getStartDate = (): Date => {
    const now = new Date();
    return startDate > now ? startDate : now;
  };

  const calculateNextSend = () => {
    let nextSendDate = new Date(getStartDate().getTime());
    if (recurrenceUnit === FrequencyUnits.Hour) {
      nextSendDate = nextSendDate.addHours(recurrence);
    } else if (recurrenceUnit === FrequencyUnits.Month) {
      nextSendDate = nextSendDate.addMonths(recurrence);
    } else if (recurrenceUnit === FrequencyUnits.Week) {
      nextSendDate = nextSendDate.addDays(7 * recurrence);
    } else if (recurrenceUnit === FrequencyUnits.Day) {
      nextSendDate = nextSendDate.addDays(recurrence);
    }

    return renderDateTime(nextSendDate);
  };

  const renderSchedule = () => {
    if (isOneTimeOnly) {
      return (
        <FormattedMessage id="outreach.form.supportOutreachTriggerSection.oneTimeOnly" />
      );
    }
    const date = new Date(getStartDate().getTime());
    const translationPrefix =
      'outreach.form.supportOutreachTriggerSection.triggerFooter.prefixes.';
    const prefixes: Record<string, string> = {
      [FrequencyUnits.Day]: 'daily',
      [FrequencyUnits.Month]: 'monthly',
      [FrequencyUnits.Hour]: 'hourly',
      [FrequencyUnits.Week]: 'weekly',
    };
    const mapFrequencyToDateRenderer: Record<string, string> = {
      [FrequencyUnits.Day]: date.toLocaleTimeString(),
      [FrequencyUnits.Month]: date.toLocaleDateString(),
      [FrequencyUnits.Hour]: `${date.toLocaleDateString()} ${date.toLocaleTimeString()}`,
      [FrequencyUnits.Week]: date.toLocaleDateString(),
    };
    const translationId = translationPrefix + prefixes[recurrenceUnit];
    return (
      <>
        <FormattedMessage id={translationId} />
        {` ${mapFrequencyToDateRenderer[recurrenceUnit]}`}
      </>
    );
  };
  const shouldShowNextSend = rawStartDate && !isOneTimeOnly;

  return (
    <div className="OutreachContainer__subscriber-outreach-trigger-row OutreachContainer__subscriber-outreach-trigger-info">
      {rawStartDate && !isOneTimeOnly && (
        <p>
          <FormattedMessage id="outreach.form.supportOutreachTriggerSection.startTime" />
          <span className="OutreachContainer__subscriber-outreach-trigger-info-value">
            {renderStartDateTime()}
          </span>
        </p>
      )}
      {!isOneTimeOnly && (
        <p>
          <FormattedMessage id="outreach.form.supportOutreachTriggerSection.endTime" />
          <span className="OutreachContainer__subscriber-outreach-trigger-info-value">
            {renderEndDateTime()}
          </span>
        </p>
      )}
      {rawStartDate && (
        <p>
          <FormattedMessage id="outreach.form.supportOutreachTriggerSection.schedule" />
          <span className="OutreachContainer__subscriber-outreach-trigger-info-value">
            {renderSchedule()}
          </span>
        </p>
      )}
      {shouldShowNextSend && (
        <p>
          <FormattedMessage id="outreach.form.supportOutreachTriggerSection.nextSend" />
          <span className="OutreachContainer__subscriber-outreach-trigger-info-value">
            {calculateNextSend()}
          </span>
        </p>
      )}
      <p>
        <FormattedMessage id="outreach.form.supportOutreachTriggerSection.clearMessage" />
        <span className="OutreachContainer__subscriber-outreach-trigger-info-value">
          <FormattedMessage id="no" />
        </span>
      </p>
    </div>
  );
};

export default memo(SupportTimeTriggerFooter);
